/**
 * TrackerWorkspace — Feature Roadmap & Bug Tracker, an in-app WORKSPACE (mirrors BillingWorkspace).
 *
 * Docks beside SiteNav (tray-aware via `.aj-tracker-ws` in outline.css). Opens on the `open-tracker`
 * event (SiteNav item), closes on `close-tracker` (SiteNav "Projects"). Active state persists so a
 * refresh restores it.
 *
 * Views: a sortable/groupable table and a status Kanban board (toggle preserves data — both read the
 * same entries state). Priority is manual order; reorder (drag handle + up/down arrows) is gated to
 * manual-order mode (sort = Priority asc, no search, no grouping) and reindexes server-side. Per-entry
 * actions: edit, delete (confirmed), and Generate Prompt (Anthropic-expanded with a template fallback).
 * All persistence runs through services/tracker.js. The Anthropic key never touches the client.
 */

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { confirmDialog, alertDialog } from './Dialog.jsx'
import MockupOutlineModal from './MockupOutlineModal.jsx'
import { hasModule, isSeatLoaded } from '../services/tenancy.js'
import { isEmbedded } from '../outline/state.js'
import { WorkspaceHeader, workspaceTabsWrapStyle, workspaceTabStyle } from './WorkspaceChrome.jsx'
import {
  listEntries, createEntry, updateEntry, reorderEntries, deleteEntry, uploadTrackerImage, generatePrompt,
  TRACKER_TYPES, TRACKER_STATUSES, TRACKER_CODEBASES,
} from '../services/tracker.js'

const STORE_KEY = 'aj:tracker:ws'
const VIEW_KEY  = 'aj:tracker:view'

const TYPE_COLOR = { 'Bug': '#c0392b', 'Feature Request': '#1D6FB8', 'Suggestion': '#1D9E75', 'Migration': '#b8860b' }
const STATUS_COLOR = { 'Backlog': '#9b8cc4', 'Planning': '#7a9aaa', 'In Progress': '#b8860b', 'Complete': '#1D9E75' }

/** Node count on an entry's sketched outline mockup (meta.mockup_outline), or 0 if none. */
function mockupCount(e) {
  return (e.meta && e.meta.mockup_outline && Array.isArray(e.meta.mockup_outline.nodes)) ? e.meta.mockup_outline.nodes.length : 0
}

/** "Jul 3, 2026, 8:15 AM" — for the "Filed by ... on ..." line. */
function fmtDateTime(v) {
  if (!v) return 'an unknown date'
  const d = new Date(v)
  return isNaN(d) ? 'an unknown date' : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

function readActive() {
  try {
    const ws = new URLSearchParams(window.location.search).get('ws')
    if (ws === 'tracker') return true
    if (ws !== null) return false   // explicit workspace in URL — don't fall back to storage
    // A peek/split iframe must never auto-restore a workspace from storage shared with the
    // parent tab — see ScheduleIntelWorkspace.jsx's own comment on this same check.
    if (isEmbedded()) return false
    const t = sessionStorage.getItem(STORE_KEY); if (t) return JSON.parse(t).active
    return JSON.parse(localStorage.getItem(STORE_KEY) || 'null')?.active || false
  } catch { return false }
}

/* ── Root ──────────────────────────────────────────────────────────────────── */

export default function TrackerWorkspace() {
  const [active, setActive] = useState(readActive)
  const [view, setView]     = useState(() => { try { return localStorage.getItem(VIEW_KEY) || 'table' } catch { return 'table' } })
  const [entries, setEntries] = useState(null)   // null = not loaded
  const [search, setSearch]   = useState('')
  const [groupBy, setGroupBy] = useState('none')  // none | type | status | codebase | priority
  const [sortKey, setSortKey] = useState('priority')
  const [sortDir, setSortDir] = useState('asc')
  const [editing, setEditing] = useState(null)    // 'new' | entry object
  const [promptFor, setPromptFor] = useState(null)
  const [seatReady, setSeatReady] = useState(isSeatLoaded)
  const loadedRef = useRef(false)

  useEffect(() => {
    function onOpen() { setActive(true) }
    function onClose() { setActive(false) }
    window.addEventListener('open-tracker', onOpen)
    window.addEventListener('close-tracker', onClose)
    return () => { window.removeEventListener('open-tracker', onOpen); window.removeEventListener('close-tracker', onClose) }
  }, [])

  // Seat data loads async at startup; on a refresh-restore the tool can't mount until it's ready.
  useEffect(() => {
    if (seatReady) return
    const id = setInterval(() => { if (isSeatLoaded()) { setSeatReady(true); clearInterval(id) } }, 100)
    return () => clearInterval(id)
  }, [seatReady])

  // Don't strand a non-manager user in an empty restored panel (e.g. a bookmarked ?ws=tracker link).
  useEffect(() => {
    if (active && seatReady && !hasModule('roadmap') && !hasModule('admin')) setActive(false)
  }, [active, seatReady])

  useEffect(() => {
    try {
      const json = JSON.stringify({ active })
      if (active) { sessionStorage.setItem(STORE_KEY, json); localStorage.setItem(STORE_KEY, json) }
      else { sessionStorage.removeItem(STORE_KEY); localStorage.removeItem(STORE_KEY) }
    } catch { /* ignore */ }
  }, [active])

  useEffect(() => {
    const url = new URL(window.location)
    if (active) {
      url.searchParams.set('ws', 'tracker')
      document.title = 'Alder James Roadmap & Bugs'
    } else {
      if (url.searchParams.get('ws') === 'tracker') url.searchParams.delete('ws')
    }
    window.history.replaceState(null, '', url)
  }, [active])

  useEffect(() => {
    if (!active) return
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [active])

  useEffect(() => { try { localStorage.setItem(VIEW_KEY, view) } catch { /* ignore */ } }, [view])

  // Load entries when the workspace becomes active (once; refresh on demand).
  useEffect(() => {
    if (!active || loadedRef.current) return
    if (!seatReady || (!hasModule('roadmap') && !hasModule('admin'))) return
    loadedRef.current = true
    reload()
  }, [active, seatReady]) // eslint-disable-line react-hooks/exhaustive-deps

  async function reload() {
    try { setEntries(await listEntries()) }
    catch (e) { setEntries([]); alertDialog({ title: 'Could not load', message: e.message || 'Failed to load tracker entries.' }) }
  }

  if (!active) return null

  const restricted = seatReady && !hasModule('roadmap') && !hasModule('admin')
  const manualMode = sortKey === 'priority' && sortDir === 'asc' && !search.trim() && groupBy === 'none'

  return (
    <div className="aj-tracker-ws" style={S.panel}>
      <WorkspaceHeader
        title="Roadmap & Bugs"
        tabs={!restricted && (
          <div style={workspaceTabsWrapStyle}>
            <button style={workspaceTabStyle(view === 'table')} onClick={() => setView('table')}>Table</button>
            <button style={workspaceTabStyle(view === 'board')} onClick={() => setView('board')}>Board</button>
          </div>
        )}
        actions={!restricted && (
          <>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…" style={S.search} />
            <button style={S.primaryBtn} onClick={() => setEditing('new')}>+ New entry</button>
          </>
        )}
      />

      {restricted ? (
        <div style={S.restricted}>You don't have access to Roadmap &amp; Bugs. Contact your administrator.</div>
      ) : (
      <div style={S.content}>
        {entries === null ? (
          <div style={S.loading}>Loading…</div>
        ) : view === 'table' ? (
          <TableView
            entries={entries} search={search} groupBy={groupBy} setGroupBy={setGroupBy}
            sortKey={sortKey} sortDir={sortDir} setSort={(k, d) => { setSortKey(k); setSortDir(d) }}
            manualMode={manualMode}
            onEdit={setEditing} onPrompt={setPromptFor} onChanged={setEntries} onReload={reload}
          />
        ) : (
          <BoardView entries={entries} search={search} onEdit={setEditing} onPrompt={setPromptFor} onChanged={setEntries} />
        )}
      </div>
      )}

      {editing && (
        <EntryFormModal
          entry={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload() }}
        />
      )}
      {promptFor && (
        <PromptModal
          entry={promptFor}
          onClose={() => setPromptFor(null)}
          onSaved={e => { setEntries(prev => (prev || []).map(x => x.id === e.id ? e : x)); setPromptFor(e) }}
        />
      )}
    </div>
  )
}

/* ── Shared helpers ────────────────────────────────────────────────────────── */

function applyFilterSort(entries, { search, sortKey, sortDir }) {
  let rows = entries.slice()
  const q = search.trim().toLowerCase()
  if (q) rows = rows.filter(e => (e.title + ' ' + (e.description || '') + ' ' + (e.ref || '') + ' ' + e.type + ' ' + e.codebase).toLowerCase().includes(q))
  const dir = sortDir === 'desc' ? -1 : 1
  rows.sort((a, b) => {
    let av = a[sortKey], bv = b[sortKey]
    if (sortKey === 'priority') return (a.priority - b.priority) * dir
    av = (av || '').toString().toLowerCase(); bv = (bv || '').toString().toLowerCase()
    return av < bv ? -1 * dir : av > bv ? 1 * dir : (a.priority - b.priority)
  })
  return rows
}

function Pill({ text, color }) {
  return <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color, background: color + '18', borderRadius: 4, padding: '1px 6px', whiteSpace: 'nowrap' }}>{text}</span>
}

/* ── Table view ────────────────────────────────────────────────────────────── */

function TableView({ entries, search, groupBy, setGroupBy, sortKey, sortDir, setSort, manualMode, onEdit, onPrompt, onChanged, onReload }) {
  const [dragId, setDragId] = useState(null)
  const rows = applyFilterSort(entries, { search, sortKey, sortDir })

  function clickSort(k) {
    if (sortKey === k) setSort(k, sortDir === 'asc' ? 'desc' : 'asc')
    else setSort(k, 'asc')
  }

  async function changeStatus(id, status) {
    const next = entries.map(e => e.id === id ? { ...e, status } : e)
    onChanged(next)
    try { await updateEntry(id, { status }) } catch (e) { onReload(); alertDialog({ title: 'Update failed', message: e.message }) }
  }

  async function move(id, delta) {
    const ordered = entries.slice().sort((a, b) => a.priority - b.priority)
    const i = ordered.findIndex(e => e.id === id)
    const j = i + delta
    if (i < 0 || j < 0 || j >= ordered.length) return
    const arr = ordered.slice();[arr[i], arr[j]] = [arr[j], arr[i]]
    onChanged(arr.map((e, idx) => ({ ...e, priority: idx + 1 })))
    try { onChanged(await reorderEntries(arr.map(e => e.id))) } catch (err) { onReload() }
  }

  async function dropOn(targetId) {
    if (!dragId || dragId === targetId) { setDragId(null); return }
    const ordered = entries.slice().sort((a, b) => a.priority - b.priority)
    const from = ordered.findIndex(e => e.id === dragId)
    const moved = ordered.splice(from, 1)[0]
    const to = ordered.findIndex(e => e.id === targetId)
    ordered.splice(to, 0, moved)
    setDragId(null)
    onChanged(ordered.map((e, idx) => ({ ...e, priority: idx + 1 })))
    try { onChanged(await reorderEntries(ordered.map(e => e.id))) } catch (err) { onReload() }
  }

  async function del(entry) {
    if (!(await confirmDialog({ title: 'Delete entry?', message: `Delete ${entry.ref} "${entry.title}"? This cannot be undone.`, confirmLabel: 'Delete', danger: true }))) return
    try { onChanged(await deleteEntry(entry.id)) } catch (e) { onReload(); alertDialog({ title: 'Delete failed', message: e.message }) }
  }

  // Group the (already filtered+sorted) rows when grouping is on.
  const groups = groupBy === 'none' ? [{ key: null, rows }] : (() => {
    const map = new Map()
    rows.forEach(e => { const k = groupBy === 'priority' ? 'Priority ' + e.priority : (e[groupBy] || '—'); if (!map.has(k)) map.set(k, []); map.get(k).push(e) })
    return [...map.entries()].map(([key, rows]) => ({ key, rows }))
  })()

  const arrow = k => sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''

  return (
    <div style={{ padding: '12px 18px 40px' }}>
      <div style={S.controls}>
        <label style={S.ctlLabel}>Group by</label>
        <select value={groupBy} onChange={e => setGroupBy(e.target.value)} style={S.select}>
          <option value="none">None</option><option value="type">Type</option><option value="status">Status</option>
          <option value="codebase">Codebase</option><option value="priority">Priority</option>
        </select>
        {!manualMode && !search.trim() && (
          <span style={S.gateHint}>Reorder is available in manual order: sort by Priority ascending, no grouping.</span>
        )}
      </div>

      <table style={S.table}>
        <thead>
          <tr>
            <th style={{ ...S.th, width: 28 }}></th>
            <th style={{ ...S.th, width: 70, cursor: 'pointer' }} onClick={() => clickSort('priority')}>#{arrow('priority')}</th>
            <th style={{ ...S.th, width: 64, cursor: 'pointer' }} onClick={() => clickSort('ref')}>ID{arrow('ref')}</th>
            <th style={{ ...S.th, cursor: 'pointer' }} onClick={() => clickSort('title')}>Title{arrow('title')}</th>
            <th style={{ ...S.th, width: 120, cursor: 'pointer' }} onClick={() => clickSort('type')}>Type{arrow('type')}</th>
            <th style={{ ...S.th, width: 150, cursor: 'pointer' }} onClick={() => clickSort('codebase')}>Codebase{arrow('codebase')}</th>
            <th style={{ ...S.th, width: 130, cursor: 'pointer' }} onClick={() => clickSort('status')}>Status{arrow('status')}</th>
            <th style={{ ...S.th, width: 96 }}></th>
          </tr>
        </thead>
        <tbody>
          {groups.map(g => (
            <GroupRows key={g.key || '_'} group={g} groupBy={groupBy} manualMode={manualMode}
              dragId={dragId} setDragId={setDragId} dropOn={dropOn} move={move}
              changeStatus={changeStatus} onEdit={onEdit} onPrompt={onPrompt} del={del} />
          ))}
          {rows.length === 0 && <tr><td colSpan={8} style={S.empty}>No entries.</td></tr>}
        </tbody>
      </table>
    </div>
  )
}

function GroupRows({ group, groupBy, manualMode, dragId, setDragId, dropOn, move, changeStatus, onEdit, onPrompt, del }) {
  return (
    <>
      {group.key !== null && (
        <tr><td colSpan={8} style={S.groupHead}>{group.key} <span style={{ color: 'var(--aj-text-muted)', fontWeight: 400 }}>· {group.rows.length}</span></td></tr>
      )}
      {group.rows.map(e => (
        <tr key={e.id}
          draggable={manualMode}
          onDragStart={manualMode ? () => setDragId(e.id) : undefined}
          onDragOver={manualMode ? ev => ev.preventDefault() : undefined}
          onDrop={manualMode ? () => dropOn(e.id) : undefined}
          style={{ ...S.tr, ...(dragId === e.id ? { opacity: 0.4 } : {}) }}
        >
          <td style={S.td}>
            {manualMode ? <span title="Drag to reorder" style={{ cursor: 'grab', color: 'var(--aj-text-muted)' }}>⠿</span> : null}
          </td>
          <td style={S.td}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 12, color: 'var(--aj-text)' }}>{e.priority}</span>
              {manualMode && (
                <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 0.7 }}>
                  <button style={S.arrowBtn} title="Move up" onClick={() => move(e.id, -1)}>▲</button>
                  <button style={S.arrowBtn} title="Move down" onClick={() => move(e.id, 1)}>▼</button>
                </span>
              )}
            </div>
          </td>
          <td style={{ ...S.td, fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'var(--aj-text-muted)' }}>{e.ref}</td>
          <td style={S.td}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }} onClick={() => onEdit(e)}>
              <span style={{ fontWeight: 500, color: 'var(--aj-text)' }}>{e.title}</span>
              {Array.isArray(e.images) && e.images.length > 0 && <span title={e.images.length + ' image(s)'} style={{ fontSize: 11, color: 'var(--aj-text-muted)' }}>🖼 {e.images.length}</span>}
              {mockupCount(e) > 0 && <span title={mockupCount(e) + '-node outline mockup'} style={{ fontSize: 11, color: 'var(--aj-text-muted)' }}>🗂 {mockupCount(e)}</span>}
            </div>
          </td>
          <td style={S.td}><Pill text={e.type} color={TYPE_COLOR[e.type] || '#7a9aaa'} /></td>
          <td style={{ ...S.td, fontSize: 12, color: 'var(--aj-text-mid)' }}>{e.codebase}</td>
          <td style={S.td}>
            <select value={e.status} onChange={ev => changeStatus(e.id, ev.target.value)} style={{ ...S.statusSelect, color: STATUS_COLOR[e.status] || 'var(--aj-text)' }}>
              {TRACKER_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </td>
          <td style={{ ...S.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
            <button style={S.miniBtn} title="Generate Claude Code prompt" onClick={() => onPrompt(e)}>Prompt</button>
            <button style={{ ...S.miniBtn, color: '#c0392b' }} title="Delete" onClick={() => del(e)}>✕</button>
          </td>
        </tr>
      ))}
    </>
  )
}

/* ── Board view ────────────────────────────────────────────────────────────── */

function BoardView({ entries, search, onEdit, onPrompt, onChanged }) {
  const [dragId, setDragId] = useState(null)
  const [overStatus, setOverStatus] = useState(null)   // column being dragged over (highlight)
  const [overCardId, setOverCardId] = useState(null)   // card we'd drop ABOVE (insertion bar)
  const [movedId, setMovedId] = useState(null)         // card that just landed (pop animation)
  const moveTimer = useRef(null)

  function endDrag() { setDragId(null); setOverStatus(null); setOverCardId(null) }

  /**
   * Drop the dragged card into `status`, positioned before `beforeId` (or appended to the end of that
   * column when beforeId is null). Priority is the global manual order, so we rebuild the whole order
   * and reindex via reorderEntries — the same mechanism the table uses. Handles both reordering within
   * a column and moving between columns (with a position) in one move.
   */
  async function handleDrop(status, beforeId) {
    const id = dragId
    endDrag()
    if (!id || id === beforeId) return
    const ordered = entries.slice().sort((a, b) => a.priority - b.priority)
    const moved = ordered.find(e => e.id === id)
    if (!moved) return

    const list = ordered.filter(e => e.id !== id)
    const movedNew = { ...moved, status }
    if (beforeId) {
      const idx = list.findIndex(e => e.id === beforeId)
      list.splice(idx < 0 ? list.length : idx, 0, movedNew)
    } else {
      // Append to the end of the target column (after its last card), or to the global end if empty.
      let lastIdx = -1
      list.forEach((e, i) => { if (e.status === status) lastIdx = i })
      list.splice(lastIdx === -1 ? list.length : lastIdx + 1, 0, movedNew)
    }

    // No-op if nothing actually changed (same column + same neighbors).
    const sameStatus = moved.status === status
    const samePos = list.findIndex(e => e.id === id) === ordered.findIndex(e => e.id === id)
    if (sameStatus && samePos) return

    onChanged(list.map((e, i) => ({ ...e, priority: i + 1 })))   // optimistic
    setMovedId(id)
    if (moveTimer.current) clearTimeout(moveTimer.current)
    moveTimer.current = setTimeout(() => setMovedId(null), 500)
    try {
      if (!sameStatus) await updateEntry(id, { status })
      onChanged(await reorderEntries(list.map(e => e.id)))
    } catch (_) { /* a reload will reconcile */ }
  }

  return (
    <div style={S.board}>
      {TRACKER_STATUSES.map(status => {
        const q = (search || '').trim().toLowerCase()
        const cards = entries
          .filter(e => e.status === status && (!q || (e.title + ' ' + (e.description || '') + ' ' + (e.ref || '') + ' ' + (e.type || '') + ' ' + (e.codebase || '')).toLowerCase().includes(q)))
          .sort((a, b) => a.priority - b.priority)
        const isOver = overStatus === status && dragId
        return (
          <div key={status}
            className={'aj-tk-col' + (isOver ? ' aj-tk-col--over' : '')}
            style={S.col}
            onDragOver={e => { e.preventDefault(); if (overStatus !== status) setOverStatus(status) }}
            onDrop={() => handleDrop(status, null)}>
            <div style={S.colHead}>
              <span style={{ color: STATUS_COLOR[status] }}>●</span> {status}
              <span style={{ color: 'var(--aj-text-muted)', marginLeft: 6 }}>{cards.length}</span>
            </div>
            <div style={S.colBody}>
              {cards.map(e => (
                <div key={e.id} draggable
                  className={'aj-tk-card' + (dragId === e.id ? ' aj-tk-card--dragging' : '') + (movedId === e.id ? ' aj-tk-card--moved' : '') + (overCardId === e.id && dragId && dragId !== e.id ? ' aj-tk-card--insert' : '')}
                  onDragStart={() => setDragId(e.id)} onDragEnd={endDrag}
                  onDragOver={ev => { ev.preventDefault(); ev.stopPropagation(); if (overStatus !== status) setOverStatus(status); if (overCardId !== e.id) setOverCardId(e.id) }}
                  onDrop={ev => { ev.stopPropagation(); handleDrop(status, e.id) }}
                  style={S.card} onClick={() => onEdit(e)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, marginBottom: 5 }}>
                    <Pill text={e.type} color={TYPE_COLOR[e.type] || '#7a9aaa'} />
                    <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-muted)' }}>{e.ref}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, fontWeight: 500, color: 'var(--aj-text)' }}>{e.title}</span>
                    {mockupCount(e) > 0 && <span title={mockupCount(e) + '-node outline mockup'} style={{ fontSize: 10.5, color: 'var(--aj-text-muted)' }}>🗂 {mockupCount(e)}</span>}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 10.5, color: 'var(--aj-text-muted)' }}>{e.codebase}</span>
                    <button style={S.miniBtn} onClick={ev => { ev.stopPropagation(); onPrompt(e) }}>Prompt</button>
                  </div>
                </div>
              ))}
              {cards.length === 0 && <div style={S.colEmpty}>{isOver ? 'Release to move here' : 'Drop here'}</div>}
            </div>
          </div>
        )
      })}
    </div>
  )
}

/* ── Entry form modal (create / edit) ──────────────────────────────────────── */

function EntryFormModal({ entry, onClose, onSaved }) {
  const isEdit = !!entry
  const [title, setTitle] = useState(entry?.title || '')
  const [description, setDescription] = useState(entry?.description || '')
  const [type, setType] = useState(entry?.type || 'Bug')
  const [codebase, setCodebase] = useState(entry?.codebase || 'AlderCortex')
  const [status, setStatus] = useState(entry?.status || 'Backlog')
  const [priority, setPriority] = useState(entry?.priority || '')
  const [images, setImages] = useState(entry?.images || [])
  const [meta, setMeta] = useState(entry?.meta || {})
  const [sketchOpen, setSketchOpen] = useState(false)
  const [lightboxImage, setLightboxImage] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [saving, setSaving] = useState(false)
  const fileRef = useRef(null)
  const mockupNodes = (meta.mockup_outline && meta.mockup_outline.nodes) || []

  async function addFiles(files) {
    const list = Array.from(files || []).filter(f => f.type.startsWith('image/'))
    if (!list.length) return
    setUploading(true)
    for (const f of list) {
      try { const im = await uploadTrackerImage(f); setImages(prev => [...prev, im]) }
      catch (e) { alertDialog({ title: 'Upload failed', message: e.message }) }
    }
    setUploading(false)
  }
  function onPaste(e) {
    const items = e.clipboardData && e.clipboardData.items
    if (!items) return
    const files = []
    for (const it of items) { if (it.kind === 'file' && it.type.startsWith('image/')) { const f = it.getAsFile(); if (f) files.push(f) } }
    if (files.length) { e.preventDefault(); addFiles(files) }
  }

  async function save() {
    if (!title.trim()) { await alertDialog({ title: 'Title required', message: 'Enter a title.' }); return }
    setSaving(true)
    const fields = { title: title.trim(), description, type, codebase, status, images, meta }
    if (priority !== '' && !isNaN(+priority)) fields.priority = +priority
    try {
      if (isEdit) await updateEntry(entry.id, fields)
      else await createEntry(fields)
      onSaved()
    } catch (e) { await alertDialog({ title: 'Save failed', message: e.message }); setSaving(false) }
  }

  return (
    <>
    {createPortal(
    <div style={M.overlay} onPaste={onPaste}>
      <div data-modal-id="TrackerEntryModal" style={M.card} role="dialog" aria-modal="true">
        <div style={M.head}>
          <span style={M.title}>{isEdit ? `Edit ${entry.ref}` : 'New entry'}</span>
          <button onClick={onClose} style={M.x}>×</button>
        </div>
        {isEdit && (
          <div style={M.filedBy}>
            Filed by {entry.created_by_name || 'Unknown'} on {fmtDateTime(entry.created_at)}
          </div>
        )}
        <div style={M.body}>
          <Field label="Title">
            <input value={title} onChange={e => setTitle(e.target.value)} autoFocus style={M.input} placeholder="Short summary" />
          </Field>
          <Field label="Description">
            <textarea value={description} onChange={e => setDescription(e.target.value)} style={{ ...M.input, minHeight: 110, resize: 'vertical' }}
              placeholder="Full detail. For bugs: current vs expected behavior, context." />
          </Field>
          <Field label="Outline mockup (optional)">
            <button type="button" style={M.ghost} onClick={() => setSketchOpen(true)}>
              {mockupNodes.length ? `Edit outline mockup (${mockupNodes.length} nodes)` : 'Sketch outline mockup'}
            </button>
          </Field>
          <div style={{ display: 'flex', gap: 10 }}>
            <Field label="Type" grow>
              <select value={type} onChange={e => setType(e.target.value)} style={M.input}>{TRACKER_TYPES.map(t => <option key={t}>{t}</option>)}</select>
            </Field>
            <Field label="Codebase" grow>
              <input list="tracker-codebases" value={codebase} onChange={e => setCodebase(e.target.value)} style={M.input} />
              <datalist id="tracker-codebases">{TRACKER_CODEBASES.map(c => <option key={c} value={c} />)}</datalist>
            </Field>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Field label="Status" grow>
              <select value={status} onChange={e => setStatus(e.target.value)} style={M.input}>{TRACKER_STATUSES.map(s => <option key={s}>{s}</option>)}</select>
            </Field>
            <Field label="Priority (blank = end)" grow>
              <input type="number" min="1" value={priority} onChange={e => setPriority(e.target.value)} style={M.input} placeholder="Auto" />
            </Field>
          </div>
          <Field label="Context images (upload, drag, or paste)">
            <div
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files) }}
              onClick={() => fileRef.current && fileRef.current.click()}
              style={M.drop}
            >
              {uploading ? 'Uploading…' : 'Click, drop, or paste images here'}
              <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={e => addFiles(e.target.files)} />
            </div>
            {images.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {images.map((im, i) => (
                  <div key={im.storage_path || i} style={M.thumbWrap}>
                    <img src={im.url} alt={im.name} style={{ ...M.thumb, cursor: 'zoom-in' }} title="Click to view full size"
                      onClick={() => setLightboxImage(im)} />
                    <button style={M.thumbX} title="Remove" onClick={() => setImages(prev => prev.filter((_, j) => j !== i))}>×</button>
                  </div>
                ))}
              </div>
            )}
          </Field>
        </div>
        <div style={M.foot}>
          <button onClick={onClose} style={M.ghost} disabled={saving}>Cancel</button>
          <button onClick={save} style={M.primary} disabled={saving || uploading}>{saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create entry'}</button>
        </div>
      </div>
    </div>,
    document.body
    )}
    {sketchOpen && (
      <MockupOutlineModal
        initialNodes={mockupNodes}
        onClose={() => setSketchOpen(false)}
        onSave={nodes => { setMeta(m => ({ ...m, mockup_outline: { nodes } })); setSketchOpen(false) }}
      />
    )}
    {lightboxImage && <ImageLightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />}
    </>
  )
}

/* ── Image lightbox — click a context-image thumbnail to view it full size, or copy it ──────── */

function ImageLightbox({ image, onClose }) {
  const [copied, setCopied] = useState(false)
  const [note, setNote] = useState('')

  useEffect(() => {
    // Capture phase so Escape closes the lightbox before any other Escape handler (e.g. the
    // outline engine's) sees it, matching JourneyPreviewOverlay.jsx's convention.
    function onKey(e) { if (e.key === 'Escape') { e.stopImmediatePropagation(); onClose() } }
    window.addEventListener('keydown', onKey, { capture: true })
    return () => window.removeEventListener('keydown', onKey, { capture: true })
  }, [onClose])

  function downloadBlob(blob) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = image.name || 'image.png'
    document.body.appendChild(a); a.click(); a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  // Same fallback chain as SketchPage.jsx's Copy PNG: unsupported API -> download; async
  // rejection -> download; sync throw -> download. Fetches the remote Storage URL into a blob
  // first since (unlike SketchPage) there's no local canvas to read from.
  async function copy() {
    setNote('')
    let blob
    try { blob = await (await fetch(image.url)).blob() }
    catch { setNote('Could not load the image to copy.'); return }

    if (!window.ClipboardItem || !navigator.clipboard || !navigator.clipboard.write) {
      downloadBlob(blob); setNote('Clipboard not supported — downloaded instead.'); return
    }
    try {
      await navigator.clipboard.write([new window.ClipboardItem({ [blob.type || 'image/png']: blob })])
      setCopied(true); setTimeout(() => setCopied(false), 1400)
    } catch {
      downloadBlob(blob); setNote('Copy failed — downloaded instead.')
    }
  }

  return createPortal(
    <div style={L.overlay} onClick={onClose}>
      <div style={L.bar} onClick={e => e.stopPropagation()}>
        <span style={L.name}>{image.name}</span>
        <div style={{ flex: 1 }} />
        {note && <span style={L.note}>{note}</span>}
        <button onClick={copy} style={L.btn}>{copied ? 'Copied ✓' : 'Copy image'}</button>
        <button onClick={onClose} style={L.btn}>Close ✕</button>
      </div>
      <div style={L.frame} onClick={e => e.stopPropagation()}>
        <img src={image.url} alt={image.name} style={L.img} />
      </div>
    </div>,
    document.body
  )
}

/* ── Generate-prompt modal ─────────────────────────────────────────────────── */

function PromptModal({ entry, onClose, onSaved }) {
  // A previously generated prompt is saved on the entry (meta.generated_prompt). Reopen shows it
  // instantly; Regenerate produces a fresh one. Nothing is lost on close.
  const savedPrompt = entry.meta && entry.meta.generated_prompt
  const [loading, setLoading] = useState(!savedPrompt)
  const [result, setResult] = useState(savedPrompt
    ? { prompt: savedPrompt, fallback: !!(entry.meta && entry.meta.generated_prompt_fallback), at: entry.meta && entry.meta.generated_prompt_at }
    : null)
  const [err, setErr] = useState('')
  const [copied, setCopied] = useState(false)

  async function generate() {
    setLoading(true); setErr('')
    try {
      const r = await generatePrompt(entry)
      setResult(r)
      setLoading(false)
      // Persist on the entry so it survives closing the dialog and page reloads.
      try {
        const meta = { ...(entry.meta || {}), generated_prompt: r.prompt, generated_prompt_fallback: !!r.fallback, generated_prompt_at: new Date().toISOString() }
        const updated = await updateEntry(entry.id, { meta })
        onSaved && onSaved(updated || { ...entry, meta })
      } catch (_) { /* still shown this session even if the save fails */ }
    } catch (e) { setErr(e.message || 'Failed to generate.'); setLoading(false) }
  }

  // Generate on first open only when there's no saved prompt.
  useEffect(() => {
    if (!savedPrompt) generate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function copy() {
    if (!result) return
    const text = result.prompt
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 1200) }
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(text).then(done).catch(() => {})
    else { const ta = document.createElement('textarea'); ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); ta.remove(); done() }
  }

  return createPortal(
    <div style={M.overlay}>
      <div data-modal-id="TrackerPromptModal" style={{ ...M.card, maxWidth: 720 }} role="dialog" aria-modal="true">
        <div style={M.head}>
          <span style={M.title}>Claude Code prompt · {entry.ref}</span>
          <button onClick={onClose} style={M.x}>×</button>
        </div>
        <div style={{ ...M.body, minHeight: 200 }}>
          {loading && <div style={{ color: 'var(--aj-text-muted)', fontSize: 13 }}>Generating prompt…</div>}
          {err && <div style={{ color: '#c0392b', fontSize: 13 }}>{err}</div>}
          {result && (
            <>
              {result.fallback && result.note && (
                <div style={M.notice}>{result.note}</div>
              )}
              <pre style={M.pre}>{result.prompt}</pre>
            </>
          )}
        </div>
        <div style={M.foot}>
          <button onClick={onClose} style={M.ghost}>Close</button>
          <button onClick={generate} style={M.ghost} disabled={loading}>{loading ? 'Generating…' : 'Regenerate'}</button>
          <button onClick={copy} style={M.primary} disabled={!result || loading}>{copied ? 'Copied ✓' : 'Copy'}</button>
        </div>
      </div>
    </div>,
    document.body
  )
}

function Field({ label, children, grow }) {
  return (
    <div style={{ marginBottom: 12, ...(grow ? { flex: 1 } : {}) }}>
      <div style={M.fieldLabel}>{label}</div>
      {children}
    </div>
  )
}

/* ── styles ────────────────────────────────────────────────────────────────── */

const S = {
  panel: { display: 'flex', flexDirection: 'column' },
  search: { width: 180, padding: '5px 9px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)' },
  primaryBtn: { fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, fontWeight: 600, color: 'var(--aj-text)', background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '4px 12px', cursor: 'pointer' },
  content: { flex: 1, overflow: 'auto', background: 'var(--aj-bg-page)' },
  restricted: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text-mid)' },
  loading: { padding: 40, textAlign: 'center', color: 'var(--aj-text-muted)', fontFamily: '"DM Sans",sans-serif', fontSize: 13 },
  controls: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 },
  ctlLabel: { fontFamily: '"DM Mono",monospace', fontSize: 10, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--aj-text-muted)' },
  select: { padding: '4px 8px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text)', background: 'var(--aj-bg-elevated)' },
  gateHint: { fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#b8860b', marginLeft: 6 },
  table: { width: '100%', borderCollapse: 'collapse', background: 'var(--aj-bg-elevated)', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  th: { textAlign: 'left', fontFamily: '"DM Mono",monospace', fontSize: 9.5, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', padding: '8px 8px', borderBottom: '0.5px solid var(--aj-border)', userSelect: 'none' },
  tr: { borderBottom: '0.5px solid var(--aj-border)' },
  td: { padding: '7px 8px', fontFamily: '"DM Sans",sans-serif', fontSize: 13, verticalAlign: 'middle' },
  groupHead: { padding: '10px 8px 4px', fontFamily: '"Montserrat",sans-serif', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--aj-text)', fontWeight: 600, background: 'var(--aj-bg-page)' },
  empty: { padding: 30, textAlign: 'center', color: 'var(--aj-text-muted)', fontFamily: '"DM Sans",sans-serif', fontSize: 13 },
  arrowBtn: { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', fontSize: 7, padding: 0, lineHeight: 1 },
  statusSelect: { padding: '3px 6px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'var(--aj-bg-elevated)', cursor: 'pointer' },
  miniBtn: { background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '3px 8px', marginLeft: 5, cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text)' },
  board: { display: 'flex', gap: 12, padding: '12px 18px 40px', alignItems: 'flex-start' },
  col: { flex: 1, minWidth: 0, background: 'var(--aj-bg-elevated)', borderRadius: 8, border: '0.5px solid var(--aj-border)', display: 'flex', flexDirection: 'column', maxHeight: '100%' },
  colHead: { padding: '10px 12px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, color: 'var(--aj-text)', borderBottom: '0.5px solid var(--aj-border)' },
  colBody: { padding: 8, display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto' },
  card: { background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 6, padding: '8px 10px', cursor: 'pointer' },
  colEmpty: { padding: '14px', textAlign: 'center', color: 'var(--aj-text-muted)', fontFamily: '"DM Sans",sans-serif', fontSize: 11, border: '1px dashed var(--aj-border)', borderRadius: 6 },
}

const M = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 700, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '5vh 16px', overflowY: 'auto' },
  card: { width: '100%', maxWidth: 560, maxHeight: '90vh', display: 'flex', flexDirection: 'column', background: 'var(--aj-bg-elevated)', borderRadius: 10, boxShadow: '0 12px 40px rgba(0,0,0,0.3)' },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '0.5px solid var(--aj-border-mid)', flexShrink: 0 },
  title: { fontFamily: 'Montserrat,sans-serif', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 13, fontWeight: 700, color: 'var(--aj-text)' },
  x: { background: 'none', border: 'none', fontSize: 22, lineHeight: 1, color: 'var(--aj-text-muted)', cursor: 'pointer' },
  filedBy: { padding: '8px 18px', fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'var(--aj-text-muted)', borderBottom: '0.5px solid var(--aj-border-mid)', flexShrink: 0 },
  body: { padding: '16px 18px', flex: '1 1 auto', minHeight: 0, overflowY: 'auto' },
  foot: { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 18px', borderTop: '0.5px solid var(--aj-border-mid)', flexShrink: 0 },
  fieldLabel: { fontFamily: '"DM Mono",monospace', fontSize: 10, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginBottom: 4 },
  input: { width: '100%', boxSizing: 'border-box', padding: '7px 9px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', background: 'var(--aj-bg-elevated)' },
  drop: { border: '1px dashed var(--aj-border-mid)', borderRadius: 6, padding: '14px', textAlign: 'center', color: 'var(--aj-text-muted)', fontFamily: '"DM Sans",sans-serif', fontSize: 12, cursor: 'pointer', background: 'var(--aj-bg-subtle)' },
  thumbWrap: { position: 'relative', width: 56, height: 56, borderRadius: 4, overflow: 'hidden', border: '0.5px solid var(--aj-border-mid)' },
  thumb: { width: '100%', height: '100%', objectFit: 'cover' },
  thumbX: { position: 'absolute', top: 1, right: 1, width: 16, height: 16, borderRadius: 3, border: 'none', background: 'rgba(0,0,0,0.7)', color: '#fff', cursor: 'pointer', fontSize: 11, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  primary: { background: '#07272D', border: 'none', borderRadius: 4, padding: '7px 16px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: '#fff' },
  ghost: { background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '7px 16px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' },
  notice: { background: 'rgba(184,134,11,0.1)', border: '0.5px solid rgba(184,134,11,0.3)', borderRadius: 6, padding: '8px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#8a6508', marginBottom: 10 },
  pre: { whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: '"DM Mono",monospace', fontSize: 12, lineHeight: 1.5, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 6, padding: '12px 14px', margin: 0 },
}

const L = {
  overlay: { position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(0,0,0,0.82)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, cursor: 'zoom-out' },
  bar: { position: 'absolute', top: 0, left: 0, right: 0, display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px', cursor: 'default' },
  name: { fontFamily: '"DM Mono",monospace', fontSize: 12, color: 'rgba(255,255,255,0.75)' },
  note: { fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#f0c869' },
  btn: { background: 'rgba(255,255,255,0.12)', border: '0.5px solid rgba(255,255,255,0.25)', borderRadius: 4, padding: '6px 12px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, fontWeight: 600, color: '#fff' },
  frame: { background: '#fff', borderRadius: 6, padding: 8, boxShadow: '0 12px 40px rgba(0,0,0,0.4)', maxWidth: '92vw', maxHeight: '84vh', cursor: 'default' },
  img: { display: 'block', maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 2 },
}
